import React, { useContext, useEffect, useState } from "react"

import { gql, useQuery } from "urql"

import {
  faDesktop,
  faLock,
  faLockOpen,
  faMicrophone,
  faMicrophoneSlash,
  faVideo,
  faVideoSlash,
} from "@fortawesome/free-solid-svg-icons"
import {
  ClipboardDocumentCheckIcon,
  ClipboardDocumentIcon,
} from "@heroicons/react/24/solid"

import { Color } from "~tailwindui/types/enums"

import { path } from "~Tutor/TutorRoutes"
import { Button, StyledLink, WithLoadingIndicator } from "~tailwindui/Basics"

import EventEmitter from "src/EventEmitter"

import useWebsocket from "hooks/useWebsocket"

import CloudroomContext from "components/ChimeCloudroom/CloudroomContext"
import BaseButton from "components/ChimeCloudroom/Shared/BaseButton"
import GoogleDocButton from "components/ChimeCloudroom/Shared/GoogleDocButton"
import MiroButton from "components/ChimeCloudroom/Shared/MiroButton"
import useLocalScreenShare from "components/ChimeCloudroom/hooks/useLocalScreenShare"

import CompleteSessionModal from "./CompleteSessionModal"
import CompleteWorksheetButtons from "./CompleteWorksheetButtons"
import SendSmsButton from "./SendSmsButton"

const Controls = () => {
  const {
    session,
    localUser,
    roster,
    allAudioMuted,
    toggleAllAudio,
    allVideoMuted,
    toggleAllVideo,
    allControlsLocked,
    toggleAllControlsLocked,
    setSurveyOpen,
  } = useContext(CloudroomContext)

  const { startLocalScreenShare, stopLocalScreenShare } = useLocalScreenShare()

  const toggleScreenShare = () => {
    screenSharing ? stopLocalScreenShare() : startLocalScreenShare()
  }

  const [result, refetchQuery] = useQuery({
    query: tutorControlsQuery,
    variables: { airtutorsSessionId: session.id },
  })

  useWebsocket({
    channelName: "AirtutorsSessionChannel",
    channelProps: { id: session.id },
  })

  useEffect(() => {
    const onUpdate = () => refetchQuery({ requestPolicy: "network-only" })
    EventEmitter.subscribe("update", onUpdate)
    return () => EventEmitter.unsubscribe("update", onUpdate)
  }, [refetchQuery])

  const screenSharing =
    roster[session.chimeJoinInfo.attendee.attendeeId]?.sharingContent

  const [linkCopied, setLinkCopied] = useState(false)

  const addToClipboard = () => {
    navigator.clipboard.writeText(session.studentCloudroomLink)
    setLinkCopied(true)
    setTimeout(() => setLinkCopied(false), 2000)
  }

  return (
    <div className="grid grid-cols-2 gap-x-2 sm:grid-cols-1">
      <WithLoadingIndicator result={result}>
        {({ data }) => (
          <>
            <BaseButton
              active={allAudioMuted}
              onClick={toggleAllAudio}
              activeText="Unmute Students"
              inactiveText="Mute Students"
              activeIcon={faMicrophoneSlash}
              inactiveIcon={faMicrophone}
              className="mb-1 w-full !rounded-lg leading-loose hover:opacity-75"
            />
            <BaseButton
              active={allVideoMuted}
              onClick={toggleAllVideo}
              activeText="Enable Student Video"
              inactiveText="Disable Student Video"
              activeIcon={faVideoSlash}
              inactiveIcon={faVideo}
              className="mb-1 w-full !rounded-lg leading-loose hover:opacity-75"
            />
            <BaseButton
              active={allControlsLocked}
              onClick={toggleAllControlsLocked}
              activeText="Unlock Student Controls"
              inactiveText="Lock Student Controls"
              activeIcon={faLock}
              inactiveIcon={faLockOpen}
              className="mb-1 w-full !rounded-lg leading-loose hover:opacity-75"
            />
            <BaseButton
              active={screenSharing}
              onClick={toggleScreenShare}
              activeText="Stop Sharing Your Screen"
              inactiveText="Share Your Screen"
              activeIcon={faDesktop}
              inactiveIcon={faDesktop}
              className="mb-1 w-full !rounded-lg leading-loose hover:opacity-75"
            />

            <CompleteWorksheetButtons
              session={data.airtutorsSession}
              refreshData={refetchQuery}
            />

            <MiroButton
              tailwind
              user={localUser}
              session={data.airtutorsSession}
            />

            <GoogleDocButton
              tailwind
              googledocUrl={data.airtutorsSession.googledocUrl}
              userType={localUser.type}
            />

            <StyledLink
              to={
                session.studyGroup
                  ? path("studyGroupDetails", {
                      id: session.studyGroup.id,
                    })
                  : path("studentDetails", {
                      id: session.students[0].id,
                    })
              }
              className="mb-1 w-full"
              target="_blank"
              rel="noopener noreferrer"
              button
              color={Color.Red}
            >
              Previous Sessions
            </StyledLink>

            {data.airtutorsSession.survey && (
              <Button
                onClick={() => setSurveyOpen(true)}
                color={Color.Orange}
                className="mb-1 w-full"
              >
                Re-Open Survey Modal
              </Button>
            )}

            <CompleteSessionModal session={data.airtutorsSession} />

            <SendSmsButton session={data.airtutorsSession} />

            <Button
              color={Color.Purple}
              className="mb-1 inline-flex w-full items-center justify-center"
              onClick={() => addToClipboard()}
            >
              {linkCopied ? "Link copied!" : "Copy Student Link"}
              {linkCopied ? (
                <ClipboardDocumentCheckIcon className="ml-1 h-6 w-6 text-green-400" />
              ) : (
                <ClipboardDocumentIcon className="ml-1 h-6 w-6" />
              )}
            </Button>
          </>
        )}
      </WithLoadingIndicator>
    </div>
  )
}

const tutorControlsQuery = gql`
  query ChimeCloudroomTutorLayoutControls($airtutorsSessionId: ID!) {
    airtutorsSession(id: $airtutorsSessionId) {
      id
      startsAt
      endsAt
      googledocUrl
      realtimeboardPending
      realtimeboards {
        id
      }
      survey {
        id
      }
      currentLessonResources {
        id
        name
        isLast
        lessonTopic {
          name
        }
      }
      studentSessions {
        id
        lateSmsSentAt
        student {
          id
          fullName
          lastName
          phoneNumber
        }
      }
    }
  }
`

export default Controls
